import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Badge from './shared/Badge';
import Card from './shared/Card';
import InfoRow from './shared/InfoRow';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';

const STORE_ICONS = {
    qdrant: 'M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125',
    s3: 'M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z',
};

function VectorStoreCard({ store }) {
    const isConnected = store.status === 'connected';
    const icon = STORE_ICONS[store.provider] || STORE_ICONS.qdrant;

    return (
        <Link to={`/admin/rag/vector-stores/${store.provider}`} className="block">
            <Card className="hover:border-gray-300 transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isConnected ? 'bg-emerald-50' : 'bg-red-50'}`}>
                            <svg className={`w-5 h-5 ${isConnected ? 'text-emerald-600' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 capitalize">{store.provider?.replace('_', ' ')}</h3>
                            <Badge variant={isConnected ? 'success' : 'danger'} dot>{isConnected ? 'Connected' : 'Error'}</Badge>
                        </div>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                </div>
                <div className="space-y-1">
                    <InfoRow label="Indexes" value={store.total_indexes} />
                    <InfoRow label="Total Vectors" value={store.total_vectors >= 0 ? store.total_vectors.toLocaleString() : 'N/A'} />
                    {store.config?.url && <InfoRow label="URL" value={store.config.url} mono />}
                    {store.config?.bucket && <InfoRow label="Bucket" value={store.config.bucket} mono />}
                    {store.config?.region && <InfoRow label="Region" value={store.config.region} />}
                </div>
            </Card>
        </Link>
    );
}

export default function AdminVectorStores() {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => { loadStores(); }, []);

    const loadStores = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await adminApi.listVectorStores();
            setStores(res.stores || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading vector stores..." center />;

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Vector Stores</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Manage and inspect vector store providers</p>
                </div>
                <Button variant="ghost" size="sm" onClick={loadStores}>Refresh</Button>
            </div>
            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {stores.map((store) => (
                    <VectorStoreCard key={store.provider} store={store} />
                ))}
            </div>
        </div>
    );
}
